import type { AlertRuleInput, AlertRuleType, AlertRuleView, AlertScopeKind, AlertSeverity } from '@chukua/common';

/** The rule form is flat: one field per threshold, only the chosen type's are read. People think in minutes, the server stores seconds. */
export interface AlertRuleFormValues {
  name: string;
  type: AlertRuleType;
  silentMinutes?: number;
  zoneKey?: string;
  direction?: 'enter' | 'exit';
  limitKph?: number;
  dropLitres?: number;
  withinMinutes?: number;
  /** Tamper causes: each is a switch; the silence one also takes a time, which the server stores as null when the switch is off. */
  onPowerDisconnect?: boolean;
  silentWithIgnitionOn?: boolean;
  silentWithIgnitionOnMinutes?: number;
  phoneMovingWhileSilent?: boolean;
  stoppedMinutes?: number;
  idleMinutes?: number;
  toleranceMeters?: number;
  /** How long off route or over the limit before a sustained rule fires. */
  minutes?: number;
  /** Optional: leave empty for no overnight arm. */
  overnightMinutes?: number | null;
  /** Contradiction rules (#92): how long GPS may be at a stop before the missing tap is flagged, how far a tap may be from its stop, and the fix window around it. */
  windowMinutes?: number;
  distanceMetres?: number;
  aroundMinutes?: number;
  /** Deadline rules (#97): the margin, in minutes, between the projected arrival and the job's deadline at or below which the rule fires. */
  slackMinutes?: number;
  /** Whether the driver is also asked to re-check their steps; off unless the rule's author turns it on. */
  nudgeDriver?: boolean;
  scopeKind: AlertScopeKind;
  scopeValues: string[];
  severity: AlertSeverity;
  notifyRoleKeys: string[];
  cooldownMinutes: number;
}

/** What a new rule of each type starts with, so switching the type in the form shows sensible values rather than empty fields. */
const NEW_TYPE_DEFAULTS = {
  dropLitres: 30,
  withinMinutes: 30,
  onPowerDisconnect: true,
  silentWithIgnitionOn: true,
  silentWithIgnitionOnMinutes: 15,
  phoneMovingWhileSilent: false,
  windowMinutes: 10,
  distanceMetres: 1000,
  aroundMinutes: 10,
  slackMinutes: 60,
  nudgeDriver: false,
};

export const NEW_RULE_VALUES: AlertRuleFormValues = {
  ...NEW_TYPE_DEFAULTS,
  name: '',
  type: 'speeding',
  limitKph: 80,
  scopeKind: 'company',
  scopeValues: [],
  severity: 'warning',
  notifyRoleKeys: [],
  cooldownMinutes: 30,
};

export function toFormValues(rule: AlertRuleView): AlertRuleFormValues {
  const { silentWithIgnitionOnMinutes, ...thresholds } = rule.thresholds as Record<string, unknown> & { silentWithIgnitionOnMinutes?: number | null };
  return {
    ...NEW_RULE_VALUES,
    ...thresholds,
    ...(rule.type === 'tracker_tamper'
      ? {
          silentWithIgnitionOn: silentWithIgnitionOnMinutes != null,
          silentWithIgnitionOnMinutes: silentWithIgnitionOnMinutes ?? NEW_TYPE_DEFAULTS.silentWithIgnitionOnMinutes,
        }
      : {}),
    name: rule.name,
    type: rule.type,
    scopeKind: rule.scopeKind,
    scopeValues: rule.scopeValues,
    severity: rule.severity,
    notifyRoleKeys: rule.notifyRoleKeys,
    cooldownMinutes: Math.max(1, Math.round(rule.cooldownSeconds / 60)),
  };
}

/** One line on when a rule fires, from its thresholds. */
export function describeThresholds(rule: AlertRuleView): string {
  const t = rule.thresholds as ThresholdFields;
  return THRESHOLD_DESCRIPTIONS[rule.type](t);
}

function describeTamperCauses(t: ThresholdFields): string {
  const causes = [
    t.onPowerDisconnect ? 'power disconnected' : null,
    t.silentWithIgnitionOnMinutes == null ? null : `silent ${t.silentWithIgnitionOnMinutes} min, ignition on`,
    t.phoneMovingWhileSilent ? 'phone moving while silent' : null,
  ];
  return `Tamper: ${causes.filter((cause) => cause !== null).join('; ')}`;
}

type ThresholdFields = Partial<Record<keyof AlertRuleFormValues, string | number | boolean | null>>;

const THRESHOLD_DESCRIPTIONS: Record<AlertRuleType, (t: ThresholdFields) => string> = {
  tracker_silent: (t) => `No signal for ${t.silentMinutes} min`,
  geofence: (t) => `${t.direction === 'enter' ? 'Enters' : 'Leaves'} zone ${t.zoneKey}`,
  speeding: (t) => `Over ${t.limitKph} km/h`,
  speeding_sustained: (t) => `Over ${t.limitKph} km/h for ${t.minutes} min`,
  stopped: (t) => `Standing still for ${t.stoppedMinutes} min`,
  idling: (t) => `Engine running, not moving, for ${t.idleMinutes} min`,
  off_route: (t) => `More than ${t.toleranceMeters} m off the route for ${t.minutes} min`,
  unsafe_stop: (t) =>
    t.overnightMinutes
      ? `Stopped ${t.stoppedMinutes} min in an unsafe area, or ${t.overnightMinutes} min overnight`
      : `Stopped ${t.stoppedMinutes} min in an unsafe area`,
  fuel_drop: (t) => `Tank falls ${t.dropLitres} L within ${t.withinMinutes} min, no refuel recorded`,
  tracker_tamper: describeTamperCauses,
  milestone_missing: (t) => `GPS at a stop ${t.windowMinutes} min with no tap${driverNote(t)}`,
  milestone_far: (t) => `Tap more than ${t.distanceMetres} m from its stop, by GPS within ${t.aroundMinutes} min${driverNote(t)}`,
  delivery_deadline: (t) => deadlineDescription('delivery', t),
  empty_return_deadline: (t) => deadlineDescription('empty-return', t),
};

/** Slack 0 is "projected late"; above it, the rule speaks before the deadline passes. */
function deadlineDescription(deadline: string, t: ThresholdFields): string {
  return t.slackMinutes ? `Projected within ${t.slackMinutes} min of the ${deadline} deadline, or late` : `Projected to miss the ${deadline} deadline`;
}

const driverNote = (t: ThresholdFields) => (t.nudgeDriver ? '; driver asked to check' : '');

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** What a rule covers, for the rules table: class codes are short enough to list, units are counted. */
export function describeScope(rule: Pick<AlertRuleView, 'scopeKind' | 'scopeValues'>): string {
  switch (rule.scopeKind) {
    case 'company':
      return 'Whole company';
    case 'truck_class':
      return `Classes: ${rule.scopeValues.join(', ')}`;
    case 'unit':
      return plural(rule.scopeValues.length, 'unit');
  }
}

const THRESHOLDS_FROM_FORM: Record<AlertRuleType, (values: AlertRuleFormValues) => object> = {
  tracker_silent: (v) => ({ silentMinutes: Number(v.silentMinutes) }),
  geofence: (v) => ({ zoneKey: v.zoneKey ?? '', direction: v.direction ?? 'exit' }),
  speeding: (v) => ({ limitKph: Number(v.limitKph) }),
  speeding_sustained: (v) => ({ limitKph: Number(v.limitKph), minutes: Number(v.minutes) }),
  stopped: (v) => ({ stoppedMinutes: Number(v.stoppedMinutes) }),
  idling: (v) => ({ idleMinutes: Number(v.idleMinutes) }),
  off_route: (v) => ({ toleranceMeters: Number(v.toleranceMeters), minutes: Number(v.minutes) }),
  unsafe_stop: (v) => unsafeStopThresholds(v),
  fuel_drop: (v) => ({ dropLitres: Number(v.dropLitres), withinMinutes: Number(v.withinMinutes) }),
  tracker_tamper: (v) => ({
    onPowerDisconnect: v.onPowerDisconnect === true,
    silentWithIgnitionOnMinutes: v.silentWithIgnitionOn ? Number(v.silentWithIgnitionOnMinutes) : null,
    phoneMovingWhileSilent: v.phoneMovingWhileSilent === true,
  }),
  milestone_missing: (v) => ({ windowMinutes: Number(v.windowMinutes), nudgeDriver: v.nudgeDriver === true }),
  milestone_far: (v) => ({ distanceMetres: Number(v.distanceMetres), aroundMinutes: Number(v.aroundMinutes), nudgeDriver: v.nudgeDriver === true }),
  delivery_deadline: (v) => ({ slackMinutes: Number(v.slackMinutes) }),
  empty_return_deadline: (v) => ({ slackMinutes: Number(v.slackMinutes) }),
};

function thresholdsFor(values: AlertRuleFormValues) {
  return { type: values.type, thresholds: THRESHOLDS_FROM_FORM[values.type](values) };
}

/** An empty overnight field means no overnight arm, so the key is left out rather than sent as null. */
function unsafeStopThresholds(values: AlertRuleFormValues) {
  const stoppedMinutes = Number(values.stoppedMinutes);
  return values.overnightMinutes ? { stoppedMinutes, overnightMinutes: Number(values.overnightMinutes) } : { stoppedMinutes };
}

/** `notifyUserIds` is passed through from the rule being edited: the form names roles only, so editing must not drop named people. */
export function toRuleInput(values: AlertRuleFormValues, notifyUserIds: string[] = []): AlertRuleInput {
  return {
    ...thresholdsFor(values),
    name: values.name,
    scopeKind: values.scopeKind,
    scopeValues: values.scopeKind === 'company' ? [] : values.scopeValues,
    severity: values.severity,
    notifyRoleKeys: values.notifyRoleKeys,
    notifyUserIds,
    cooldownSeconds: values.cooldownMinutes * 60,
  } as AlertRuleInput;
}
